import React, { useEffect, useState, useMemo } from "react";
import useSWR from "swr";
import TypeIcon from "../../../svgs/type.svg";
import CashReceive from "../../../svgs/cash-receive.svg";
import Reload from "../../../svgs/reload.svg";
import DocumentIcon from "../../../svgs/document-upload.svg";
import DocumentUpload from "../../Common/DocumentUpload";
import { useNotification } from "../../../../Notifications/NotificationProvider";
import {
  inventoryFetcher,
  InventoryService,
} from "../../../network/InventoryService";
import FormField from "../../../../Shared/Form/FormField";

const CreateChargingPlanModal = ({
  showChargingPlanCreateModal,
  setShowChargingPlanCreateModal,
  isDetailsMode = false,
  planData = null,
  onPlanCreated,
}) => {
  const dispatchNotification = useNotification();
  const [form, setForm] = useState({
    planName: "",
    device: "",
    planType: "",
    upFrontCharge: "",
    emiChargePerInstallment: "",
    emiInstallmentCount: "",
    emiChargingFrequency: "",
  });
  const [formError, setFormError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);

  const devicesApiUrl = useMemo(() => {
    const params = new URLSearchParams({
      is_dropdown: true,
      inventory_type: "device",
    });

    return `${
      process.env.REACT_APP_API_URL
    }/api/payment_speaker/v1/psb-model?${params.toString()}`;
  }, []);

  const {
    data: devicesResponse,
    error: devicesError,
    isLoading: devicesLoading,
  } = useSWR(devicesApiUrl, inventoryFetcher, {
    fallbackData: {},
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });

  const devices = devicesResponse?.data || [];

  const deviceOptions = [
    {
      value: "",
      label: devicesLoading
        ? "Loading devices..."
        : devicesError
          ? "Error loading devices"
          : "Select a device...",
      isPlaceholder: true,
    },
    ...devices.map((device) => ({
      value: device.id.toString(),
      label: `${device.name} - ${device.brand} ${device.model}`,
    })),
  ];

  const planTypeOptions = [
    { value: "", label: "Select One", isPlaceholder: true },
    { value: "mixed", label: "Mixed" },
    { value: "full_upfront", label: "Full Upfront" },
    { value: "full_emi", label: "EMI Only" },
    { value: "free", label: "Free" },
  ];

  const frequencyOptions = [
    { value: "", label: "Monthly", isPlaceholder: true },
    { value: "Monthly", label: "Monthly" },
    { value: "Weekly", label: "Weekly" },
    { value: "Quarterly", label: "Quarterly" },
    { value: "Yearly", label: "Yearly" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "application/pdf" || file.name.endsWith(".pdf")) {
        setUploadedFile(file);
      } else {
        alert("Please upload only PDF files.");
      }
    }
    event.target.value = "";
  };

  const handleDeleteFile = () => {
    setUploadedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.planName) {
      setFormError("Please fill Plan Name.");
      return;
    }

    if (!form.device) {
      setFormError("Please select a Device.");
      return;
    }

    if (!form.planType) {
      setFormError("Please select a Plan Type.");
      return;
    }

    if (form.planType === "full_upfront" && !form.upFrontCharge) {
      setFormError("Please fill Up Front Charge for Full Upfront plan.");
      return;
    }

    if (
      form.planType === "full_emi" &&
      (!form.emiChargePerInstallment ||
        !form.emiInstallmentCount ||
        !form.emiChargingFrequency)
    ) {
      setFormError("Please fill all EMI fields for EMI Only plan.");
      return;
    }

    if (
      form.planType === "mixed" &&
      (!form.upFrontCharge ||
        !form.emiChargePerInstallment ||
        !form.emiInstallmentCount ||
        !form.emiChargingFrequency)
    ) {
      setFormError("Please fill all pricing fields for Mixed plan.");
      return;
    }
    if (!uploadedFile) {
      setFormError("Please upload NFA Attachment.");
      return;
    }
    setIsCreating(true);
    setFormError("");

    try {
      const planRequestData = {
        name: form.planName,
        scheme_type: form.planType,
        device_model: form.device,
        up_front_amount: parseFloat(form.upFrontCharge) || 0,
        emi_amount: parseFloat(form.emiChargePerInstallment) || 0,
        emi_count: parseInt(form.emiInstallmentCount) || 0,
        emi_frequency: form.emiChargingFrequency,
        attachment: uploadedFile,
      };

      const response =
        await InventoryService.createChargingPlan(planRequestData);

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Charging plan created successfully!",
        });

        setForm({
          planName: "",
          device: "",
          planType: "",
          upFrontCharge: "",
          emiChargePerInstallment: "",
          emiInstallmentCount: "",
          emiChargingFrequency: "",
        });
        setUploadedFile(null);

        setShowChargingPlanCreateModal(false);

        if (onPlanCreated) {
          onPlanCreated();
        }
      } else {
        throw new Error(response.message || "Failed to create charging plan");
      }
    } catch (error) {
      console.error("Error creating charging plan:", error);

      let errorMessage = "Failed to create charging plan. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async () => {
    if (!planData?.id) {
      setFormError("No plan ID found for deactivation.");
      return;
    }

    setIsDeactivating(true);
    setFormError("");

    try {
      const response = await InventoryService.deactivateChargingPlan(
        planData.id
      );

      if (response.is_error === 0) {
        dispatchNotification({
          type: "SUCCESS",
          message: "Charging plan deactivated successfully!",
        });

        setShowChargingPlanCreateModal(false);

        if (onPlanCreated) {
          onPlanCreated();
        }
      } else {
        throw new Error(
          response.message || "Failed to deactivate charging plan"
        );
      }
    } catch (error) {
      console.error("Error deactivating charging plan:", error);

      let errorMessage =
        "Failed to deactivate charging plan. Please try again.";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setFormError(errorMessage);

      dispatchNotification({
        type: "ERROR",
        message: errorMessage,
      });
    } finally {
      setIsDeactivating(false);
    }
  };

  const renderDynamicFields = () => {
    if (!form.planType || form.planType === "free") return null;

    switch (form.planType) {
      case "full_upfront":
        return (
          <FormField
            id="upFrontCharge"
            name="upFrontCharge"
            title="Up Front Charge"
            type="number"
            value={form.upFrontCharge}
            onChange={handleInputChange}
            placeholder="2000"
            icon={CashReceive}
            disabled={isDetailsMode || isCreating || isDeactivating}
            isFlat
            required
          />
        );

      case "full_emi":
        return (
          <>
            <FormField
              id="emiChargePerInstallment"
              name="emiChargePerInstallment"
              title="EMI Charge Per Installment (BDT)"
              type="number"
              value={form.emiChargePerInstallment}
              onChange={handleInputChange}
              placeholder="500"
              icon={CashReceive}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />

            <FormField
              id="emiInstallmentCount"
              name="emiInstallmentCount"
              title="EMI Installment Count"
              type="number"
              value={form.emiInstallmentCount}
              onChange={handleInputChange}
              placeholder="3"
              icon={Reload}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />

            <FormField
              id="emiChargingFrequency"
              name="emiChargingFrequency"
              title="EMI Charging Frequency"
              type="select"
              value={form.emiChargingFrequency}
              onChange={handleInputChange}
              options={frequencyOptions}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />
          </>
        );

      case "mixed":
        return (
          <>
            <FormField
              id="upFrontCharge"
              name="upFrontCharge"
              title="Up Front Charge"
              type="number"
              value={form.upFrontCharge}
              onChange={handleInputChange}
              placeholder="2000"
              icon={CashReceive}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />

            <FormField
              id="emiChargePerInstallment"
              name="emiChargePerInstallment"
              title="EMI Charge Per Installment (BDT)"
              type="number"
              value={form.emiChargePerInstallment}
              onChange={handleInputChange}
              placeholder="500"
              icon={CashReceive}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />
            <FormField
              id="emiInstallmentCount"
              name="emiInstallmentCount"
              title="EMI Installment Count"
              type="number"
              value={form.emiInstallmentCount}
              onChange={handleInputChange}
              placeholder="3"
              icon={Reload}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />
            <FormField
              id="emiChargingFrequency"
              name="emiChargingFrequency"
              title="EMI Charging Frequency"
              type="select"
              value={form.emiChargingFrequency}
              onChange={handleInputChange}
              options={frequencyOptions}
              disabled={isDetailsMode || isCreating || isDeactivating}
              isFlat
              required
            />
          </>
        );

      default:
        return null;
    }
  };

  const shouldShowTwoColumns = form.planType && form.planType !== "free";

  useEffect(() => {
    if (isDetailsMode && planData) {
      setForm({
        planName: planData.name || "",
        device: planData.device_model__name || "",
        planType: planData.scheme_type || "",
        upFrontCharge: planData.up_front_amount || "",
        emiChargePerInstallment: planData.emi_amount || "",
        emiInstallmentCount: planData.emi_count || "",
        emiChargingFrequency: planData.emi_charging_frequency || "Monthly",
      });

      if (planData.attachment) {
        setUploadedFile(planData.attachment);
      }
    }
  }, [isDetailsMode, planData]);

  return (
    <div
      className="inventory-modal-overlay"
      onClick={() =>
        !isCreating && !isDeactivating && setShowChargingPlanCreateModal(false)
      }
    >
      <div
        className="charging-plan-modal inventory-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inventory-modal-header">
          <h3>
            {isDetailsMode ? "Payment Plan Details" : "Add New Payment Plan"}
          </h3>
          <button
            className="close-btn"
            type="button"
            onClick={() =>
              !isCreating &&
              !isDeactivating &&
              setShowChargingPlanCreateModal(false)
            }
            disabled={isCreating || isDeactivating}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="inventory-modal-body">
            <div className="inventory-modal-body-content">
              <div className="plan-info-section">
                <FormField
                  id="planName"
                  name="planName"
                  title="Plan Name"
                  type="text"
                  value={form.planName}
                  onChange={handleInputChange}
                  placeholder="Enter plan name"
                  disabled={isDetailsMode || isCreating || isDeactivating}
                  isFlat
                  required
                />

                <FormField
                  id="device"
                  name="device"
                  title="Device"
                  type="select"
                  value={form.device}
                  onChange={handleInputChange}
                  options={deviceOptions}
                  disabled={
                    isDetailsMode ||
                    isCreating ||
                    isDeactivating ||
                    devicesLoading ||
                    devicesError
                  }
                  isFlat
                  required
                />

                <FormField
                  id="planType"
                  name="planType"
                  title="Plan Type"
                  type="select"
                  value={form.planType}
                  onChange={handleInputChange}
                  options={planTypeOptions}
                  disabled={isDetailsMode || isCreating || isDeactivating}
                  isFlat
                  required
                />

                <div className="plan-upload-section">
                  <DocumentUpload
                    uploadTitle="Upload NFA Attachment"
                    downloadTitle={
                      isDetailsMode && !uploadedFile
                        ? "No Attachment"
                        : "Download NFA Attachment"
                    }
                    attachment={uploadedFile}
                    handleAttachmentChange={handleFileUpload}
                    allowedFileTypes=".pdf,.doc,.docx"
                    disabled={
                      isCreating ||
                      isDeactivating ||
                      (isDetailsMode && !uploadedFile)
                    }
                    downloadMode={isDetailsMode}
                    documentIcon={DocumentIcon}
                    setAttachment={!isDetailsMode ? handleDeleteFile : null}
                  />
                </div>
              </div>

              {shouldShowTwoColumns && (
                <div className="plan-pricing-section">
                  {renderDynamicFields()}
                </div>
              )}
            </div>
          </div>

          {formError && <div className="form-error">{formError}</div>}

          <div className="inventory-modal-footer">
            <button
              className="cancel-btn"
              type="button"
              onClick={() => setShowChargingPlanCreateModal(false)}
              disabled={isCreating || isDeactivating}
            >
              Cancel
            </button>
            {isDetailsMode ? (
              <button
                className="deactivate-btn"
                type="button"
                onClick={handleDeactivate}
                disabled={isCreating || isDeactivating}
              >
                {isDeactivating ? "Deactivating..." : "Deactivate"}
              </button>
            ) : (
              <button
                className="submit-btn"
                type="submit"
                disabled={isCreating || isDeactivating}
              >
                {isCreating ? "Creating..." : "Create"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChargingPlanModal;
